import React, { useState, useEffect } from "react";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";
import BreadCrumb from "Common/BreadCrumb";
import { useUserInfo } from "context/user";
import { useAppServices } from "hook/services";

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  roles: string[];
  roleType: string[];
  permissions: string[];
  status: string;
  lastLoginAt: string;
  createdAt: string;
  userType?: string;
  userAccessType?: string;
  isActive?: boolean;
}

interface UserProfileForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface ChangePasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const UserProfile = () => {
  const { user, Update } = useUserInfo();
  const AppService = useAppServices();
  const [currentUser, setCurrentUser] = useState<User | null>(user);
  const [updating, setUpdating] = useState<boolean>(false);
  const [changingPassword, setChangingPassword] = useState<boolean>(false);

  const updateProfile = async (userData: UserProfileForm) => {
    try {
      setUpdating(true);
      const payload = {
        ...userData,
        username:`${userData?.firstName} ${userData?.lastName}`,
        _id: user?._id,
      };
      const { response } = await AppService.user.update({
        payload,
        toaster:true
      });
      if (response) {
        Update(response.data);
      }
    } catch (error) {
      // console.error("Error updating profile:", error);
      return false;
    } finally {
      setUpdating(false);
    }
  };

  const changePassword = async (passwordData: ChangePasswordForm) => {
    try {
      setChangingPassword(true);
      // Replace with your actual API endpoint
    const payload = {
        ...passwordData,
        _id: user?._id,
      };
      const { response } = await AppService.user.change_password({
        payload,
        toaster:true
      });

      if (response) {
        passwordValidation.resetForm();
        return true;
      }
    } catch (error) {
      return false;
    } finally {
      setChangingPassword(false);
    }
  };

  const profileValidation = useFormik({
    enableReinitialize: true,

    initialValues: {
      firstName: currentUser?.firstName || "",
      lastName: currentUser?.lastName || "",
      email: currentUser?.email || "",
      phone: currentUser?.phone ||null,
    } as UserProfileForm,

    validationSchema: Yup.object({
      firstName: Yup.string()
        .optional()
        .min(2, "First name must be at least 2 characters")
        .max(50, "First name must not exceed 50 characters"),
      lastName: Yup.string()
        .optional()
        .min(2, "Last name must be at least 2 characters")
        .max(50, "Last name must not exceed 50 characters"),
      email: Yup.string()
        .required("Please Enter Your Email")
        .email("Please enter a valid email"),
      phone: Yup.string()
        .matches(/^[+]?[\d\s\-()]+$/, "Please enter a valid phone number")
        .nullable(),
    }),

    onSubmit: async (values) => {
      await updateProfile(values);
    },
  });

  const passwordValidation = useFormik({
    initialValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    } as ChangePasswordForm,

    validationSchema: Yup.object({
      currentPassword: Yup.string()
        .required("Please enter your current password")
        .min(6, "Password must be at least 6 characters"),
      newPassword: Yup.string()
        .required("Please enter new password")
        .min(6, "Password must be at least 6 characters")
        .notOneOf(
          [Yup.ref("currentPassword")],
          "New password must be different from current password"
        ),
      confirmPassword: Yup.string()
        .required("Please confirm your password")
        .oneOf([Yup.ref("newPassword")], "Passwords must match"),
    }),

    onSubmit: async (values) => {
      await changePassword(values);
    },
  });

  return (
    <React.Fragment>
      <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
        <BreadCrumb title="User Profile" pageTitle="Profile" />

        <div className="grid grid-cols-1 gap-5 mt-5 xl:grid-cols-2">
          {/* Personal Information */}
          <div className="card">
            <div className="card-body">
              <h5 className="mb-4 text-lg font-semibold">
                Personal Information
              </h5>

              <form onSubmit={profileValidation.handleSubmit}>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="mb-4">
                    <label className="inline-block mb-2 text-base font-medium">
                      First Name
                    </label>
                    <input
                      name="firstName"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 w-full"
                      placeholder="Enter First Name"
                      type="text"
                      onChange={profileValidation.handleChange}
                      onBlur={profileValidation.handleBlur}
                      value={profileValidation.values.firstName}
                      disabled={updating}
                    />
                    {profileValidation.touched.firstName &&
                    profileValidation.errors.firstName ? (
                      <div className="mt-1 text-sm text-red-500">
                        {profileValidation.errors.firstName}
                      </div>
                    ) : null}
                  </div>

                  <div className="mb-4">
                    <label className="inline-block mb-2 text-base font-medium">
                      Last Name
                    </label>
                    <input
                      name="lastName"
                      className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 w-full"
                      placeholder="Enter Last Name"
                      type="text"
                      onChange={profileValidation.handleChange}
                      onBlur={profileValidation.handleBlur}
                      value={profileValidation.values.lastName}
                      disabled={updating}
                    />
                    {profileValidation.touched.lastName &&
                    profileValidation.errors.lastName ? (
                      <div className="mt-1 text-sm text-red-500">
                        {profileValidation.errors.lastName}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mb-4">
                  <label className="inline-block mb-2 text-base font-medium">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="email"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 w-full"
                    placeholder="Enter Email Address"
                    type="email"
                    onChange={profileValidation.handleChange}
                    onBlur={profileValidation.handleBlur}
                    value={profileValidation.values.email}
                    disabled={updating}
                  />
                  {profileValidation.touched.email &&
                  profileValidation.errors.email ? (
                    <div className="mt-1 text-sm text-red-500">
                      {profileValidation.errors.email}
                    </div>
                  ) : null}
                </div>

                <div className="mb-4">
                  <label className="inline-block mb-2 text-base font-medium">
                    Phone Number
                  </label>
                  <input
                    name="phone"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 w-full"
                    placeholder="Enter Phone Number"
                    type="tel"
                    onChange={profileValidation.handleChange}
                    onBlur={profileValidation.handleBlur}
                    value={profileValidation.values.phone}
                    disabled={updating}
                  />
                  {profileValidation.touched.phone &&
                  profileValidation.errors.phone ? (
                    <div className="mt-1 text-sm text-red-500">
                      {profileValidation.errors.phone}
                    </div>
                  ) : null}
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    disabled={updating}
                  >
                    {updating ? "Updating..." : "Update Profile"}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Change Password */}
          <div className="card">
            <div className="card-body">
              <h5 className="mb-4 text-lg font-semibold">Change Password</h5>
              <form onSubmit={passwordValidation.handleSubmit}>
                <div className="mb-4">
                  <label className="inline-block mb-2 text-base font-medium">
                    Current Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="currentPassword"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 w-full"
                    placeholder="Enter current password"
                    type="password"
                    onChange={passwordValidation.handleChange}
                    onBlur={passwordValidation.handleBlur}
                    value={passwordValidation.values.currentPassword}
                    disabled={changingPassword}
                  />
                  {passwordValidation.touched.currentPassword &&
                  passwordValidation.errors.currentPassword ? (
                    <div className="mt-1 text-sm text-red-500">
                      {passwordValidation.errors.currentPassword}
                    </div>
                  ) : null}
                </div>

                <div className="mb-4">
                  <label className="inline-block mb-2 text-base font-medium">
                    New Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="newPassword"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 w-full"
                    placeholder="Enter new password"
                    type="password"
                    onChange={passwordValidation.handleChange}
                    onBlur={passwordValidation.handleBlur}
                    value={passwordValidation.values.newPassword}
                    disabled={changingPassword}
                  />
                  {passwordValidation.touched.newPassword &&
                  passwordValidation.errors.newPassword ? (
                    <div className="mt-1 text-sm text-red-500">
                      {passwordValidation.errors.newPassword}
                    </div>
                  ) : null}
                </div>

                <div className="mb-4">
                  <label className="inline-block mb-2 text-base font-medium">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    name="confirmPassword"
                    className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200 w-full"
                    placeholder="Confirm new password"
                    type="password"
                    onChange={passwordValidation.handleChange}
                    onBlur={passwordValidation.handleBlur}
                    value={passwordValidation.values.confirmPassword}
                    disabled={changingPassword}
                  />
                  {passwordValidation.touched.confirmPassword &&
                  passwordValidation.errors.confirmPassword ? (
                    <div className="mt-1 text-sm text-red-500">
                      {passwordValidation.errors.confirmPassword}
                    </div>
                  ) : null}
                </div>

                <div className="flex justify-end gap-2 mt-6">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm font-medium text-slate-500 bg-white border border-slate-300 rounded-md shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-custom-500 dark:bg-zink-700 dark:text-zink-200 dark:border-zink-500 dark:hover:bg-zink-600"
                    onClick={() => passwordValidation.resetForm()}
                    disabled={changingPassword}
                  >
                    Clear
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                    disabled={changingPassword}
                  >
                    {changingPassword ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
